import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MarkersAPI } from '../../api/MarkerEndpoints';
import PageTitle from '../../components/PageTitle';
import {
  ArrowIcon, CalendarIcon, CheckIcon, FlagIcon, ImageIcon, PlusIcon, TasksIcon, UploadIcon, WandIcon,
} from '../../layout/icons';
import { Avatar, MilestoneIcon } from '../team-plan/parts';
import { formatDay, nextMilestone, progress, timeLeft, upcoming } from '../team-plan/board';
import './Overview.css';

const DUE_SHOWN = 5;

const greeting = (now = new Date()) => {
  const hour = now.getHours();
  if (hour < 11) return 'Guten Morgen';
  if (hour < 18) return 'Hallo';
  return 'Guten Abend';
};

const isoDay = (date) => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

/** One thing that can be waiting: how many, what it means, where to deal with it. */
function TaskCard({ to, icon: Icon, title, count, text, doneText, extra }) {
  const done = count === 0;
  return (
    <Link to={to} className={'ov-task' + (done ? ' is-done' : '')}>
      <span className="ov-task__icon"><Icon /></span>
      <span className="ov-task__body">
        <span className="ov-task__title">{title}</span>
        <span className="ov-task__text">{done ? doneText : text}</span>
        {extra && !done && <span className="ov-task__extra">{extra}</span>}
      </span>
      <span className="ov-task__count">{count == null ? '–' : done ? <CheckIcon /> : count}</span>
      <span className="ov-task__go" aria-hidden="true"><ArrowIcon /></span>
    </Link>
  );
}

/** The team's next deadlines and the milestone it works towards, each leading to the board. */
function TeamDue({ tasks, milestones }) {
  // undefined while the counts load, null when the board could not be read.
  const soon = tasks ? upcoming(tasks).slice(0, DUE_SHOWN) : null;
  const milestone = tasks && milestones ? nextMilestone(milestones, tasks) : null;
  const reach = milestone ? progress(milestone.id, tasks) : null;
  const left = milestone ? timeLeft(milestone.dueDate) : null;

  return (
    <div className="ov-team">
      <div className="ov-due">
        {tasks === undefined && <p className="ov-due__empty">Lade Aufgaben …</p>}
        {tasks === null && <p className="ov-due__empty">Aufgaben konnten nicht geladen werden.</p>}
        {soon && soon.length === 0 && <p className="ov-due__empty">Keine offenen Aufgaben mit Datum.</p>}
        {soon && soon.length > 0 && (
          <ul className="ov-due__list">
            {soon.map((t) => {
              const when = timeLeft(t.dueDate);
              return (
                <li key={t.id}>
                  <Link to="/tasks" className="ov-due__row">
                    <span className="ov-due__title">{t.title}</span>
                    <span className="ov-due__person"><Avatar name={t.assignee} size={20} />{t.assignee || 'Niemand'}</span>
                    <span className={`ov-due__when tp-tone tp-tone--${when.tone}`}>{when.text}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
        <Link to="/tasks" className="ov-due__all">Alle Aufgaben <ArrowIcon /></Link>
      </div>

      <Link to="/tasks" className="ov-ms">
        <span className="ov-ms__label">Nächster Meilenstein</span>
        {milestone ? (
          <>
            <span className="ov-ms__title"><MilestoneIcon />{milestone.title}</span>
            <span className="ov-ms__due">
              bis {formatDay(milestone.dueDate)} · <span className={`tp-tone tp-tone--${left.tone}`}>{left.text}</span>
            </span>
            <span className="tp-bar" aria-hidden="true"><span style={{ width: `${reach.pct}%` }} /></span>
            <span className="ov-ms__count">
              {reach.total ? `${reach.done} von ${reach.total} Aufgaben erledigt` : 'Noch keine Aufgaben zugeordnet'}
            </span>
          </>
        ) : (
          <span className="ov-ms__empty">
            {tasks === undefined ? '–' : 'Kein offener Meilenstein. Leg auf der Aufgaben-Seite einen an.'}
          </span>
        )}
      </Link>
    </div>
  );
}

export default function Overview({ counts, userName }) {
  const [markers, setMarkers] = useState(null);

  useEffect(() => {
    let alive = true;
    MarkersAPI.getAllMarkers({ adminAccepted: true, size: 5000 })
      .then((data) => { if (alive) setMarkers(Array.isArray(data?.items) ? data.items : []); })
      .catch(() => { if (alive) setMarkers([]); });
    return () => { alive = false; };
  }, []);

  const figures = useMemo(() => {
    if (!markers) return null;
    const today = new Date();
    const todayIso = isoDay(today);
    const weekIso = isoDay(new Date(today.getTime() + 7 * 86400000));
    const events = markers.filter((m) => m.markerType === 'Event');
    return {
      today: events.filter((m) => m.eventDate === todayIso).length,
      week: events.filter((m) => m.eventDate >= todayIso && m.eventDate <= weekIso).length,
      clubs: markers.filter((m) => m.markerType === 'Club').length,
    };
  }, [markers]);

  const totals = counts?.rightsTotals;
  const upcomingAll = totals ? totals.upcomingVisible + totals.upcomingHidden : 0;
  const imageShare = upcomingAll ? Math.round((totals.upcomingVisible / upcomingAll) * 100) : null;
  const firstName = (userName || '').split(' ')[0];

  return (
    <div className="page ov">
      <div className="page-header">
        <PageTitle
          title={`${greeting()}${firstName ? `, ${firstName}` : ''}`}
          description="Hier siehst du, was gerade zu tun ist. Ein Klick auf eine Karte führt direkt dorthin."
        />
      </div>

      <section aria-labelledby="ov-tasks">
        <h2 id="ov-tasks" className="ov-section-title">Zu erledigen</h2>
        <div className="ov-tasks">
          <TaskCard to="/pending-events" icon={CheckIcon} title="Freigaben" count={counts?.approvals}
            text="Neue Events und Änderungen von Hosts warten auf deine Freigabe. Erst danach sind sie in der App."
            doneText="Alles freigegeben."
            extra={counts?.hostEdits ? `davon ${counts.hostEdits} ${counts.hostEdits === 1 ? 'Änderung' : 'Änderungen'} von Hosts` : null} />
          <TaskCard to="/data-quality" icon={WandIcon} title="Datenqualität" count={counts?.proposals}
            text="Der automatische Check schlägt Korrekturen vor, z. B. Uhrzeit oder Preis. Übernehmen oder verwerfen."
            doneText="Keine offenen Vorschläge." />
          {counts?.rightsToAsk === 0 && counts?.rightsToClear > 0 ? (
            <TaskCard to="/image-rights" icon={ImageIcon} title="Bildrechte" count={counts.rightsToClear}
              text="Bilder von Veranstaltern mit Zusage warten auf deine Freigabe – jedes Bild einzeln."
              doneText="Alle Bilder geprüft."
              extra={counts.rightsWaiting ? `${counts.rightsWaiting} warten auf Antwort` : null} />
          ) : (
            <TaskCard to="/image-rights" icon={ImageIcon} title="Bildrechte" count={counts?.rightsToAsk}
              text="Veranstalter, die du noch nicht gefragt hast, ob Schick ihre Bilder zeigen darf."
              doneText="Alle Veranstalter gefragt, alle Bilder geprüft."
              extra={[
                counts?.rightsToClear ? `${counts.rightsToClear} ${counts.rightsToClear === 1 ? 'Bild' : 'Bilder'} mit Zusage noch freizugeben` : null,
                counts?.rightsWaiting ? `${counts.rightsWaiting} warten auf Antwort` : null,
              ].filter(Boolean).join(' · ') || null} />
          )}
          <TaskCard to="/issues" icon={FlagIcon} title="Meldungen" count={counts?.openIssues}
            text="Offene Meldungen von Nutzern und eigene Aufgaben."
            doneText="Keine offenen Meldungen." />
          <TaskCard to="/tasks" icon={TasksIcon} title="Aufgaben" count={counts?.teamDue}
            text="Aufgaben im Team, die in den nächsten 7 Tagen fällig oder schon überfällig sind."
            doneText="Diese Woche ist im Team nichts fällig."
            extra={counts?.teamOverdue ? `davon ${counts.teamOverdue} überfällig` : null} />
        </div>
      </section>

      <section aria-labelledby="ov-due">
        <h2 id="ov-due" className="ov-section-title">Bald fällig</h2>
        <TeamDue tasks={counts?.teamTasks} milestones={counts?.teamMilestones} />
      </section>

      <section aria-labelledby="ov-glance">
        <h2 id="ov-glance" className="ov-section-title">Auf einen Blick</h2>
        <div className="ov-figures">
          <div className="ov-figure"><span className="ov-figure__value">{figures ? figures.today : '–'}</span><span className="ov-figure__label">Events heute</span></div>
          <div className="ov-figure"><span className="ov-figure__value">{figures ? figures.week : '–'}</span><span className="ov-figure__label">Events in den nächsten 7 Tagen</span></div>
          <div className="ov-figure"><span className="ov-figure__value">{figures ? figures.clubs : '–'}</span><span className="ov-figure__label">Clubs in der App</span></div>
          <div className="ov-figure"><span className="ov-figure__value">{imageShare == null ? '–' : `${imageShare}%`}</span><span className="ov-figure__label">kommende Events mit echtem Bild</span></div>
        </div>
      </section>

      <section aria-labelledby="ov-actions">
        <h2 id="ov-actions" className="ov-section-title">Schnell erledigt</h2>
        <div className="ov-actions">
          <Link to="/manage-events" className="ov-action"><PlusIcon /> Event anlegen</Link>
          <Link to="/create-club" className="ov-action"><PlusIcon /> Club anlegen</Link>
          <Link to="/events" className="ov-action"><UploadIcon /> Events per CSV importieren</Link>
          <Link to="/events" className="ov-action"><CalendarIcon /> Alle Events & Clubs</Link>
        </div>
      </section>
    </div>
  );
}
